/**
 * Rondevu API Client - RPC interface
 */
import { WebCryptoAdapter } from '../crypto/web.js';
import { RpcBatcher } from './batcher.js';
/**
 * RondevuAPI - RPC-based API client for Rondevu signaling server
 *
 * Uses Ed25519 public key cryptography for authentication.
 * The public key IS the identity (like Ethereum addresses).
 */
export class RondevuAPI {
    constructor(baseUrl, keyPair, cryptoAdapter, batcherOptions) {
        this.baseUrl = baseUrl;
        this.keyPair = keyPair;
        // Use WebCryptoAdapter by default (browser environment)
        this.crypto = cryptoAdapter || new WebCryptoAdapter();
        // Create batcher for request batching with throttling
        this.batcher = new RpcBatcher(baseUrl, batcherOptions);
        // Validate public key format
        if (!keyPair.publicKey || typeof keyPair.publicKey !== 'string') {
            throw new Error('Invalid keypair: publicKey must be a non-empty string');
        }
        if (keyPair.publicKey.length !== RondevuAPI.PUBLIC_KEY_LENGTH) {
            throw new Error(`Invalid keypair: publicKey must be ${RondevuAPI.PUBLIC_KEY_LENGTH} hex characters (32 bytes)`);
        }
        if (!/^[0-9a-fA-F]+$/.test(keyPair.publicKey)) {
            throw new Error('Invalid keypair: publicKey must contain only hexadecimal characters');
        }
        // Validate private key format
        if (!keyPair.privateKey || typeof keyPair.privateKey !== 'string') {
            throw new Error('Invalid keypair: privateKey must be a non-empty string');
        }
        if (keyPair.privateKey.length !== RondevuAPI.PRIVATE_KEY_LENGTH) {
            throw new Error(`Invalid keypair: privateKey must be ${RondevuAPI.PRIVATE_KEY_LENGTH} hex characters (32 bytes)`);
        }
        if (!/^[0-9a-fA-F]+$/.test(keyPair.privateKey)) {
            throw new Error('Invalid keypair: privateKey must contain only hexadecimal characters');
        }
    }
    /**
     * Canonical JSON serialization with sorted keys
     * Ensures deterministic output regardless of property insertion order
     */
    canonicalJSON(obj, depth = 0) {
        // Prevent stack overflow from deeply nested objects
        if (depth > RondevuAPI.MAX_CANONICALIZE_DEPTH) {
            throw new Error('Object nesting too deep for canonicalization');
        }
        // Handle null
        if (obj === null) {
            return 'null';
        }
        // Handle undefined
        if (obj === undefined) {
            return JSON.stringify(undefined);
        }
        // Validate primitive types
        const type = typeof obj;
        // Reject unsupported types
        if (type === 'function') {
            throw new Error('Functions are not supported in RPC parameters');
        }
        if (type === 'symbol' || type === 'bigint') {
            throw new Error(`${type} is not supported in RPC parameters`);
        }
        // Validate numbers (reject NaN and Infinity)
        if (type === 'number' && !Number.isFinite(obj)) {
            throw new Error('NaN and Infinity are not supported in RPC parameters');
        }
        // Handle primitives (string, number, boolean)
        if (type !== 'object') {
            return JSON.stringify(obj);
        }
        // Handle arrays recursively
        if (Array.isArray(obj)) {
            return '[' + obj.map(item => this.canonicalJSON(item, depth + 1)).join(',') + ']';
        }
        // Handle objects - sort keys alphabetically for deterministic output
        const sortedKeys = Object.keys(obj).sort();
        const pairs = sortedKeys.map(key => {
            return JSON.stringify(key) + ':' + this.canonicalJSON(obj[key], depth + 1);
        });
        return '{' + pairs.join(',') + '}';
    }
    /**
     * Build signature message following server format
     * Format: timestamp:nonce:method:canonicalJSON(params || {})
     *
     * Uses canonical JSON (sorted keys) to ensure deterministic serialization
     * across different JavaScript engines and platforms.
     *
     * Note: When params is undefined, it's serialized as "{}" (empty object).
     * This matches the server's expectation for parameterless RPC calls.
     */
    buildSignatureMessage(timestamp, nonce, method, params) {
        if (!method || typeof method !== 'string') {
            throw new Error('Invalid method: must be a non-empty string');
        }
        const paramsJson = this.canonicalJSON(params || {});
        return `${timestamp}:${nonce}:${method}:${paramsJson}`;
    }
    /**
     * Generate cryptographically secure nonce
     * Uses crypto.randomUUID() if available, falls back to secure random bytes
     *
     * Note: this.crypto is always initialized in constructor (WebCryptoAdapter or NodeCryptoAdapter)
     * and TypeScript enforces that both implement randomBytes(), so the fallback is always safe.
     */
    generateNonce() {
        // Get crypto object from global scope (supports various contexts)
        // In browsers: window.crypto or self.crypto
        // In modern environments: global crypto
        const globalCrypto = typeof crypto !== 'undefined'
            ? crypto
            : (typeof window !== 'undefined' && window.crypto) ||
                (typeof self !== 'undefined' && self.crypto) ||
                undefined;
        // Prefer crypto.randomUUID() for widespread support and standard format
        // UUIDv4 provides 122 bits of entropy (6 fixed version/variant bits)
        if (globalCrypto && typeof globalCrypto.randomUUID === 'function') {
            return globalCrypto.randomUUID();
        }
        // Fallback: 16 random bytes (128 bits entropy) as hex string
        // Slightly more entropy than UUID, but both are cryptographically secure
        // Safe because this.crypto is guaranteed to implement CryptoAdapter interface
        const randomBytes = this.crypto.randomBytes(16);
        return this.crypto.bytesToHex(randomBytes);
    }
    /**
     * Generate authentication headers for RPC request
     * Uses Ed25519 signature with nonce for replay protection
     *
     * Security notes:
     * - Nonce: Cryptographically secure random value (UUID or 128-bit hex)
     * - Timestamp: Prevents replay attacks outside the server's time window
     *   - Server validates timestamp is within acceptable range (typically ±5 minutes)
     *   - Tolerates reasonable clock skew between client and server
     *   - Requests with stale timestamps are rejected
     * - Signature: Ed25519 ensures message integrity and authenticity
     * - Server validates nonce uniqueness to prevent replay within time window
     *   - Each nonce can only be used once within the timestamp validity window
     *   - Server maintains nonce cache with expiration matching timestamp window
     */
    async generateAuthHeaders(request) {
        const timestamp = Date.now();
        const nonce = this.generateNonce();
        // Build message and generate Ed25519 signature
        const message = this.buildSignatureMessage(timestamp, nonce, request.method, request.params);
        const signature = await this.crypto.signMessage(this.keyPair.privateKey, message);
        return {
            'X-PublicKey': this.keyPair.publicKey,
            'X-Timestamp': timestamp.toString(),
            'X-Nonce': nonce,
            'X-Signature': signature,
        };
    }
    /**
     * Execute RPC call via batcher
     * Requests are batched with throttling for efficiency
     */
    async rpc(request, authHeaders) {
        return this.batcher.add(request, authHeaders);
    }
    // ============================================
    // Identity Management (Ed25519 Public Key)
    // ============================================
    /**
     * Generate a new Ed25519 keypair locally
     * This is completely client-side - no server communication
     *
     * @param cryptoAdapter - Optional crypto adapter (defaults to WebCryptoAdapter)
     * @returns Generated keypair with publicKey and privateKey as hex strings
     */
    static async generateKeyPair(cryptoAdapter) {
        const adapter = cryptoAdapter || new WebCryptoAdapter();
        return adapter.generateKeyPair();
    }
    // ============================================
    // Tags-based Offer Management (v2)
    // ============================================
    /**
     * Publish offers with tags
     */
    async publish(request) {
        const rpcRequest = {
            method: 'publishOffer',
            params: {
                tags: request.tags,
                offers: request.offers,
                ttl: request.ttl,
            },
        };
        const authHeaders = await this.generateAuthHeaders(rpcRequest);
        return await this.rpc(rpcRequest, authHeaders);
    }
    /**
     * Discover offers by tags
     * @param request - Discovery request with tags and optional pagination
     * @returns Paginated response if limit provided, single offer if not
     */
    async discover(request) {
        const rpcRequest = {
            method: 'discover',
            params: {
                tags: request.tags,
                limit: request.limit,
                offset: request.offset,
            },
        };
        const authHeaders = await this.generateAuthHeaders(rpcRequest);
        return await this.rpc(rpcRequest, authHeaders);
    }
    /**
     * Delete an offer by ID
     */
    async deleteOffer(offerId) {
        const request = {
            method: 'deleteOffer',
            params: { offerId },
        };
        const authHeaders = await this.generateAuthHeaders(request);
        return await this.rpc(request, authHeaders);
    }
    // ============================================
    // WebRTC Signaling
    // ============================================
    /**
     * Answer an offer
     * @param offerId The offer ID to answer
     * @param sdp The SDP answer
     * @param matchedTags Optional tags that were used to discover this offer
     */
    async answerOffer(offerId, sdp, matchedTags) {
        const request = {
            method: 'answerOffer',
            params: { offerId, sdp, matchedTags },
        };
        const authHeaders = await this.generateAuthHeaders(request);
        await this.rpc(request, authHeaders);
    }
    /**
     * Get answer for a specific offer (offerer polls this)
     */
    async getOfferAnswer(offerId) {
        try {
            const request = {
                method: 'getOfferAnswer',
                params: { offerId },
            };
            const authHeaders = await this.generateAuthHeaders(request);
            return await this.rpc(request, authHeaders);
        }
        catch (err) {
            if (err.message.includes('not yet answered')) {
                return null;
            }
            throw err;
        }
    }
    /**
     * Combined polling for answers and ICE candidates
     */
    async poll(since) {
        const request = {
            method: 'poll',
            params: { since },
        };
        const authHeaders = await this.generateAuthHeaders(request);
        return await this.rpc(request, authHeaders);
    }
    /**
     * Add ICE candidates to a specific offer
     */
    async addOfferIceCandidates(offerId, candidates) {
        const request = {
            method: 'addIceCandidates',
            params: { offerId, candidates },
        };
        const authHeaders = await this.generateAuthHeaders(request);
        return await this.rpc(request, authHeaders);
    }
    /**
     * Get ICE candidates for a specific offer
     */
    async getOfferIceCandidates(offerId, since = 0) {
        const request = {
            method: 'getIceCandidates',
            params: { offerId, since },
        };
        const authHeaders = await this.generateAuthHeaders(request);
        const result = await this.rpc(request, authHeaders);
        return {
            candidates: result.candidates || [],
            offerId: result.offerId,
        };
    }
}
// Key length constants
RondevuAPI.PUBLIC_KEY_LENGTH = 64; // 32 bytes = 64 hex chars
RondevuAPI.PRIVATE_KEY_LENGTH = 64; // 32 bytes = 64 hex chars
RondevuAPI.MAX_CANONICALIZE_DEPTH = 100; // Prevent stack overflow
